import {
  CHAT_BUDGET_OPTIONS,
  CHAT_SERVICE_OPTIONS,
  CHAT_TIMEFRAME_OPTIONS,
} from "./config";
import {
  FLOW_STEPS,
  type ChatAnswers,
  type FlowStep,
} from "./flow";

export const CHAT_DRAFT_STORAGE_KEY = "studio-tche-chat-draft-v1";
export const CHAT_DRAFT_TTL_MS = 24 * 60 * 60 * 1000;

export type DraftStep = Exclude<FlowStep, "welcome" | "success">;

export interface ChatDraft {
  readonly version: 1;
  readonly savedAt: number;
  readonly expiresAt: number;
  readonly step: DraftStep;
  readonly submissionId: string;
  readonly answers: ChatAnswers;
}

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const isAllowedString = (
  value: unknown,
  allowed: readonly string[],
): value is string => typeof value === "string" && allowed.includes(value);

const isDraftStep = (value: unknown): value is DraftStep =>
  typeof value === "string" &&
  value !== "welcome" &&
  value !== "success" &&
  (FLOW_STEPS as readonly string[]).includes(value);

const parseAnswers = (value: unknown): ChatAnswers | null => {
  if (!isRecord(value) || !Array.isArray(value.services)) return null;
  if (
    !value.services.every((service) =>
      isAllowedString(service, CHAT_SERVICE_OPTIONS),
    ) ||
    typeof value.project !== "string" ||
    value.project.length > 1000 ||
    !isAllowedString(value.timeframe, ["", ...CHAT_TIMEFRAME_OPTIONS]) ||
    !isAllowedString(value.budget, ["", ...CHAT_BUDGET_OPTIONS]) ||
    typeof value.name !== "string" ||
    value.name.length > 120 ||
    typeof value.phone !== "string" ||
    value.phone.length > 30 ||
    typeof value.email !== "string" ||
    value.email.length > 120
  ) {
    return null;
  }

  return {
    services: [...value.services],
    project: value.project,
    timeframe: value.timeframe,
    budget: value.budget,
    name: value.name,
    phone: value.phone,
    email: value.email,
  } as ChatAnswers;
};

export const parseChatDraft = (
  value: unknown,
  now = Date.now(),
): ChatDraft | null => {
  if (
    !isRecord(value) ||
    value.version !== 1 ||
    typeof value.savedAt !== "number" ||
    !Number.isFinite(value.savedAt) ||
    typeof value.expiresAt !== "number" ||
    !Number.isFinite(value.expiresAt) ||
    value.savedAt > now + 60_000 ||
    value.expiresAt <= now ||
    value.expiresAt - value.savedAt > CHAT_DRAFT_TTL_MS ||
    !isDraftStep(value.step) ||
    typeof value.submissionId !== "string" ||
    !UUID_PATTERN.test(value.submissionId)
  ) {
    return null;
  }

  const answers = parseAnswers(value.answers);
  if (!answers) return null;

  return {
    version: 1,
    savedAt: value.savedAt,
    expiresAt: value.expiresAt,
    step: value.step,
    submissionId: value.submissionId,
    answers,
  };
};

export const createChatDraft = (
  step: DraftStep,
  answers: ChatAnswers,
  submissionId: string,
  expiresAt: number,
  now = Date.now(),
): ChatDraft | null =>
  parseChatDraft(
    {
      version: 1,
      savedAt: now,
      expiresAt,
      step,
      submissionId,
      answers,
    },
    now,
  );
